import { useEffect, useRef, useState } from 'react';
import Card from '../../components/ui/Card';
import { useMediaQuery } from '../../hooks/useMediaQuery';

/** Matches `.layout-sidebar` width at the 768px desktop split. */
const SIDEBAR_WIDTH_PX = 220;

/**
 * Category — a top-level grouping of related component demos, with an anchor
 * the fixed nav links to. One per purpose bucket (Actions, Forms, …).
 */
export function Category({ id, title, children }) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      style={{ scrollMarginTop: 'calc(var(--gallery-toolbar-height, 8rem) + 0.5rem)', marginBottom: '2.75rem' }}
    >
      <h2
        id={`${id}-heading`}
        style={{
          margin: '0 0 1rem',
          paddingBottom: '0.5rem',
          fontSize: '0.8125rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
          borderBottom: '1px solid var(--border-subtle, var(--border-color))',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * GalleryNav — fixed in-page table of contents (sticky cannot work: html/body
 * set overflow-x: hidden, same reason the landing nav is fixed). Plain hash
 * anchors so the embedded page's link-blocking never intercepts them.
 *
 * children — optional title row pinned above the chips.
 * embedded — in-app /dev/components: offset past the 220px desktop sidebar.
 */
export function GalleryNav({ categories, embedded = false, children }) {
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(0);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const sync = () => {
      const height = el.offsetHeight;
      setBarHeight(height);
      const parent = el.parentElement;
      if (parent) parent.style.setProperty('--gallery-toolbar-height', `${height}px`);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => {
      observer.disconnect();
      el.parentElement?.style.removeProperty('--gallery-toolbar-height');
    };
  }, []);

  return (
    <>
      <div
        ref={barRef}
        style={{
          position: 'fixed',
          top: 0,
          left: embedded && !isNarrow ? SIDEBAR_WIDTH_PX : 0,
          right: 0,
          zIndex: 20,
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-subtle, var(--border-color))',
          boxShadow: 'var(--shadow-sm)',
          padding: isNarrow ? '0.75rem 1rem' : '0.75rem 2rem',
        }}
      >
        {children}
        <nav
          aria-label="Component categories"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.375rem',
            paddingTop: children ? '0.75rem' : 0,
          }}
        >
          {categories.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                letterSpacing: '0.03em',
                padding: '0.375rem 0.75rem',
                borderRadius: '999px',
                color: 'var(--text-secondary)',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle, var(--border-color))',
                textDecoration: 'none',
              }}
            >
              {c.label}
            </a>
          ))}
        </nav>
      </div>
      <div style={{ height: barHeight, marginBottom: '1.5rem' }} aria-hidden="true" />
    </>
  );
}

/** Section shell for a single component — title, source path, card body. */
export function Section({ name, file, children }) {
  return (
    <section style={{ marginBottom: '1.5rem' }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', color: 'var(--brand-primary)' }}>{name}</h3>
        <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{file}</code>
      </div>
      <Card>{children}</Card>
    </section>
  );
}

/** Labelled horizontal row used by most kit demos. */
export function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.5rem 0' }}>
      <span style={{ width: 170, flex: '0 0 170px', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/** Small uppercase caption above a stacked demo card in a column. */
export function ColumnLabel({ children }) {
  return (
    <span
      style={{
        fontSize: '0.6875rem',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
      }}
    >
      {children}
    </span>
  );
}
